import React, { FC, useState } from 'react';
import cx from 'classnames';
import Text, { TextSize } from '@move-ui/text';
import { ChevronSmallFilledIcon } from '@move-ui/icons-asset-catalog';
import FooterLink from './FooterLink';
import styles from './FooterLinks.css';
import { FooterLinkSection, NavigationClickHandler } from '../../types';
import { useCurrentLocale, useTranslation } from '../../utils/translations';
import getLocalizedUrl from '../../utils/url';

type Props = {
  sections?: FooterLinkSection[];
  onClick?: NavigationClickHandler;
  classNames?: {
    root?: string;
    section?: string;
    header?: string;
    item?: string;
  };
};

const FooterLinks: FC<Props> = ({ sections, onClick, classNames }) => {
  const [selectedColumn, setSelectedColumn] = useState<number | null>(null);
  const locale = useCurrentLocale();
  const { translate } = useTranslation();

  const handleOnClick = (index: number) => () => {
    setSelectedColumn(index === selectedColumn ? null : index);
  };

  const getLinkUrl = (
    url: string | Record<string, string>
  ): string | undefined =>
    typeof url === 'string' ? translate(url) : getLocalizedUrl(url, locale);

  return (
    <div
      className={cx(styles.component, classNames?.root)}
      data-testid="footer-links-root"
    >
      {sections?.map((section, index) => (
        <div
          className={cx(
            styles.column,
            {
              [styles.active]: selectedColumn === index
            },
            classNames?.section
          )}
          key={section.label}
          data-testid={`footer-links-${section.testId || index}`}
        >
          <Text
            bold
            className={cx(styles.columnTitle, classNames?.header)}
            color="neutral-100"
            onClick={handleOnClick(index)}
            size={TextSize.XS}
            data-testid={`footer-links-text-${section.testId || index}`}
          >
            <ChevronSmallFilledIcon className={styles.openIcon} />
            {translate(section.label)}
          </Text>
          {section.items.map((item, itemIndex) => (
            <FooterLink
              label={translate(item.label)}
              id={item.id}
              name={item.name}
              type={item.type}
              crawlable={item.crawlable}
              testId={item.testId}
              key={item.label}
              onClick={onClick}
              url={getLinkUrl(item.url) ?? ''}
              alt={item.alt ? translate(item.alt) : item.alt}
              className={classNames?.item}
              index={itemIndex}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default FooterLinks;
